import React, { useState } from "react";
import { Listbox } from "@headlessui/react";
import {
  FiCheck,
  FiChevronDown,
  FiCopy,
  FiCreditCard,
  FiHash,
  FiSend,
} from "react-icons/fi";
import transactionService from "../../../api/services/transactionService";

const Deposite = ({
  t,
  accountList,
  userDetails,
  setIsLoading,
  setIsModalOpen,
  setMessage,
  handleSongClick,
}) => {
  const [transactionNo, setTransactionNo] = useState("");
  const [amount, setAmount] = useState("");
  const [errorText, setErrorText] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    handleSongClick();

    if (!selectedOption) {
      setErrorText("Please select an account.");
      return;
    }
    if (transactionNo.length !== 6) {
      setErrorText("Please enter the last 6 digits of the transaction number.");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setErrorText("Please enter a valid amount.");
      return;
    }

    try {
      setErrorText("");
      setIsLoading(true);
      const response = await transactionService.createDeposit({
        userName: userDetails.username,
        userId: Number(userDetails.userId),
        transactionNo,
        remark: "",
        amount: Number(amount),
        bankAccountId: Number(selectedOption.bankAccountId),
        isAdmin: false,
      });

      setMessage(response.message ? t("transactionPending") : t("processSuccessful"));
      setTransactionNo("");
      setAmount("");
      setSelectedOption(null);
      setIsModalOpen(true);
    } catch (error) {
      setMessage(t("sendingFail"));
      setIsModalOpen(true);
      console.error("API request error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!selectedOption) {
      setErrorText("Please select an account first.");
      return;
    }
    try {
      await navigator.clipboard.writeText(selectedOption.bankAccountNumber);
      setErrorText("");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (error) {
      setErrorText("Unable to copy the account number.");
    }
  };

  return (
    <section className="overflow-visible rounded-lg border border-white/15 bg-header_green/45 shadow-2xl shadow-black/25 backdrop-blur-xl">
      <div className="border-b border-white/10 px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-300/15 text-emerald-200">
            <FiCreditCard className="h-5 w-5" />
          </div>
          <h2 className="text-base font-semibold leading-[1.8]">{t("deposite")}</h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3.5 p-4">
        <AccountSelect
          t={t}
          accountList={accountList}
          selectedOption={selectedOption}
          setSelectedOption={(option) => {
            setSelectedOption(option);
            setErrorText("");
            setCopied(false);
          }}
          onCopy={handleCopy}
          copied={copied}
        />

        <Field
          id="transactionNo"
          label={t("transactionNo")}
          value={transactionNo}
          onChange={(event) => setTransactionNo(event.target.value.slice(0, 6))}
          icon={FiHash}
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="000000"
        />
        <Field
          id="depositAmount"
          label={t("amount")}
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          icon={FiCreditCard}
          inputMode="decimal"
          type="number"
          min="1"
          placeholder="0"
        />

        {errorText && (
          <div role="alert" className="rounded-lg border border-red-300/20 bg-red-400/10 px-3 py-2.5 text-xs leading-[1.7] text-red-100">
            {errorText}
          </div>
        )}

        <button
          type="submit"
          className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold leading-[1.8] text-header_green shadow-lg transition hover:bg-emerald-50 active:scale-[0.98]"
        >
          <FiSend className="h-4 w-4" />
          {t("send")}
        </button>
      </form>
    </section>
  );
};

const Field = ({ id, label, icon: Icon, ...inputProps }) => (
  <label htmlFor={id} className="block">
    <span className="mb-1.5 block text-xs font-medium leading-[1.8] text-white/75">{label}</span>
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
      <input
        id={id}
        required
        className="w-full rounded-lg border border-white/15 bg-header_green/55 py-3 pl-10 pr-4 text-sm leading-[1.7] text-white outline-none transition placeholder:text-white/30 focus:border-emerald-300/60 focus:bg-header_green/70 focus:ring-2 focus:ring-emerald-300/10"
        {...inputProps}
      />
    </div>
  </label>
);

const AccountSelect = ({ t, accountList, selectedOption, setSelectedOption, onCopy, copied }) => (
  <div>
    <span className="mb-1.5 block text-xs font-medium leading-[1.8] text-white/75">{t("selectAccount")}</span>
    <Listbox value={selectedOption} onChange={setSelectedOption}>
      <div className="relative">
        <Listbox.Button className="flex min-h-[58px] w-full items-center rounded-lg border border-white/15 bg-header_green/55 px-3.5 py-2.5 pr-20 text-left outline-none transition focus:border-emerald-300/60 focus:ring-2 focus:ring-emerald-300/10">
          {selectedOption ? (
            <AccountDetails account={selectedOption} />
          ) : (
            <span className="text-sm leading-[1.8] text-white/45">{t("selectAccount")}</span>
          )}
          <FiChevronDown className="absolute right-4 h-4 w-4 text-white/45" />
        </Listbox.Button>

        {selectedOption && (
          <button
            type="button"
            onClick={onCopy}
            aria-label="Copy account number"
            className="absolute right-10 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-white/55 transition hover:bg-white/10 hover:text-white"
          >
            {copied ? <FiCheck className="text-emerald-300" /> : <FiCopy />}
          </button>
        )}

        <Listbox.Options className="absolute z-50 mt-2 max-h-64 w-full overflow-auto rounded-lg border border-white/10 bg-header_green p-1.5 shadow-2xl outline-none">
          {accountList.length ? (
            accountList.map((account) => (
              <Listbox.Option key={account.bankAccountId} value={account}>
                {({ active, selected }) => (
                  <div className={`relative cursor-pointer rounded-lg px-3 py-2.5 pr-9 transition ${active ? "bg-white/10" : ""}`}>
                    <AccountDetails account={account} />
                    {selected && <FiCheck className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-300" />}
                  </div>
                )}
              </Listbox.Option>
            ))
          ) : (
            <p className="px-3 py-4 text-center text-xs font-sans text-white/50">No accounts available</p>
          )}
        </Listbox.Options>
      </div>
    </Listbox>
  </div>
);

const AccountDetails = ({ account }) => (
  <div className="flex min-w-0 items-center gap-3">
    <div className="flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-lg bg-white p-1.5">
      <img src={`/logo/${account.bankType}.png`} alt="" className="h-full w-full object-contain" />
    </div>
    <div className="min-w-0 font-sans">
      <p className="truncate text-sm font-medium text-white">{account.bankAccountName}</p>
      <p className="mt-0.5 truncate text-xs tracking-wide text-white/50">{account.bankAccountNumber}</p>
    </div>
  </div>
);

export default Deposite;
